"use client";

import { useEffect, useState, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Link from "next/link";
import { ArrowLeft, User, Calendar, Clock } from "lucide-react";
import type { Insight } from "@/data/insights";
import DOMPurify from 'isomorphic-dompurify';
import toast from 'react-hot-toast';

interface Props {
  art: Insight;
  recommended: Insight[];
}

const EASE_OUT = "power3.out";

export default function InsightPageContent({ art, recommended }: Props) {
  const heroImage = art.coverImage;
  const contentImage = art.featuredImage;

  const [tocItems, setTocItems] = useState<{ id: string; text: string; level: number }[]>([]);

  // Refs for animations
  const backBtnRef = useRef<HTMLDivElement>(null);

  const handleShare = (social: string) => {
    if (typeof window === 'undefined') return;
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(art.title || '');

    switch (social) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${text}`, '_blank', 'width=600,height=400');
        break;
      case 'link':
        navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied to clipboard!");
        break;
    }
  };
  const heroCardRef = useRef<HTMLDivElement>(null);
  const heroTagsRef = useRef<HTMLDivElement>(null);
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const heroSummaryRef = useRef<HTMLParagraphElement>(null);

  const promiseSpanRef = useRef<HTMLSpanElement>(null);
  const promiseH2Ref = useRef<HTMLHeadingElement>(null);
  const promisePRef = useRef<HTMLParagraphElement>(null);
  const promiseCTARef = useRef<HTMLDivElement>(null);
  const promiseRightRef = useRef<HTMLDivElement>(null);

  const [backLink, setBackLink] = useState({ href: "/insights", text: "Back to Insights" });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const from = searchParams.get('from');

    if (from === 'home') {
      setBackLink({ href: "/", text: "Back to Home" });
    } else if (from === 'services') {
      setBackLink({ href: "/services", text: "Back to Services" });
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, 50);

    // Generate Dynamic Table of Contents from rendered HTML
    const headings = Array.from(document.querySelectorAll('.prose-content h2, .prose-content h3'));
    const items = headings.map((h, i) => {
      if (!h.id) h.id = `heading-${i}`;
      return {
        id: h.id,
        text: h.textContent || '',
        level: h.tagName === 'H3' ? 3 : 2
      };
    });
    setTocItems(items);

    return () => clearTimeout(timer);
  }, [art.content]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Hero Animations
      if (backBtnRef.current) gsap.fromTo(backBtnRef.current, { opacity: 0, x: -10 }, { opacity: 1, x: 0, duration: 0.5 });
      if (heroCardRef.current) gsap.fromTo(heroCardRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease: EASE_OUT });
      if (heroTagsRef.current) gsap.fromTo(heroTagsRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.2 });
      if (heroTitleRef.current) gsap.fromTo(heroTitleRef.current, { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.3 });
      if (heroSummaryRef.current) gsap.fromTo(heroSummaryRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.4 });

      // Promise Section Scroll Animations
      if (promiseSpanRef.current) gsap.fromTo(promiseSpanRef.current, { opacity: 0 }, { opacity: 1, duration: 0.5, scrollTrigger: { trigger: promiseSpanRef.current, start: "top 90%", once: true } });
      if (promiseH2Ref.current) gsap.fromTo(promiseH2Ref.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.5, scrollTrigger: { trigger: promiseH2Ref.current, start: "top 90%", once: true } });
      if (promisePRef.current) gsap.fromTo(promisePRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.1, scrollTrigger: { trigger: promisePRef.current, start: "top 90%", once: true } });
      if (promiseCTARef.current) gsap.fromTo(promiseCTARef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.2, scrollTrigger: { trigger: promiseCTARef.current, start: "top 90%", once: true } });
      if (promiseRightRef.current) gsap.fromTo(promiseRightRef.current, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.6, delay: 0.2, scrollTrigger: { trigger: promiseRightRef.current, start: "top 90%", once: true } });
    });

    return () => ctx.revert();
  }, []);

  const scrollToHeading = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-[#0d9488]/20 selection:text-[#0d9488] pt-40 md:pt-[200px]">

      {/* ─── Hero Banner Section ──────────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        {/* Back Button */}
        <div ref={backBtnRef} className="mb-10 ml-2 opacity-0">
          <Link href={backLink.href} className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-[#64748b] hover:text-[#0d9488] transition-colors group relative z-50 cursor-pointer py-4 pr-8 -ml-4">
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            <span>{backLink.text}</span>
          </Link>
        </div>

        {/* Hero Card */}
        <div
          ref={heroCardRef}
          className="relative w-full rounded-[2rem] md:rounded-[3rem] overflow-hidden bg-[#0f172a] shadow-2xl min-h-[400px] md:min-h-[500px] flex items-center opacity-0"
        >
          {/* Background Image with Dark Gradient Overlay */}
          {heroImage && (
            <>
              <img src={heroImage} alt={art.title} crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-contain" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a] via-[#0f172a]/70 to-transparent"></div>
            </>
          )}

          <div className="relative z-10 p-8 sm:p-12 md:p-16 lg:p-20 max-w-4xl text-white">
            <div ref={heroTagsRef} className="flex flex-wrap items-center gap-4 mb-6 opacity-0">
              <span className="bg-transparent border border-white/20 text-white/90 text-[10px] sm:text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full backdrop-blur-sm">
                {art.category}
              </span>
              <span className="text-white/70 text-sm font-medium">{art.date}</span>
              <span className="flex items-center gap-1.5 text-white/70 text-sm font-medium before:content-['•'] before:mr-2 before:text-white/30">
                <Clock className="w-3.5 h-3.5" /> {art.readTime}
              </span>
            </div>

            <h1 ref={heroTitleRef} className="text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-extrabold leading-[1.1] tracking-tight mb-6 opacity-0">
              {art.title}
            </h1>

            {(art.subtitle || art.summary) && (
              <p ref={heroSummaryRef} className="text-lg md:text-xl text-white/70 font-medium leading-relaxed max-w-2xl mb-8 opacity-0">
                {art.subtitle || art.summary}
              </p>
            )}

          </div>
        </div>
      </div>

      {/* ─── 3-Column Layout ──────────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-24 flex flex-col lg:flex-row gap-8 lg:gap-12 relative items-start">

        {/* Left Sidebar: Social Share (Sticky) */}
        <aside className="hidden lg:flex flex-col items-center gap-4 w-[60px] shrink-0 sticky top-32">
          <span className="text-xs font-bold text-slate-400 mb-2 uppercase tracking-widest">Share</span>
          {['twitter', 'facebook', 'linkedin', 'link'].map((social, i) => (
            <button key={social} onClick={() => handleShare(social)} aria-label={`Share on ${social}`} className="w-10 h-10 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center text-slate-400 hover:text-[#0d9488] hover:border-[#0d9488]/30 hover:bg-teal-50 transition-all hover:-translate-y-1">
              {social === 'twitter' && <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" /></svg>}
              {social === 'facebook' && <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" /></svg>}
              {social === 'linkedin' && <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" /></svg>}
              {social === 'link' && <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>}
            </button>
          ))}
        </aside>

        {/* Center: Main Content */}
        <div className="flex-1 min-w-0 bg-white rounded-3xl p-6 sm:p-8 md:p-12 shadow-sm border border-slate-100">

          {contentImage && (
            <div className="mb-12">
              <img src={contentImage} alt={art.title} crossOrigin="anonymous" className="w-full h-auto rounded-2xl shadow-lg" />
            </div>
          )}

          {/* Render HTML Content */}
          <div className="prose-content prose prose-lg prose-slate max-w-none break-words prose-headings:text-[#0f172a] prose-headings:font-bold prose-a:text-[#0d9488] prose-a:no-underline hover:prose-a:underline prose-img:rounded-2xl prose-img:shadow-md prose-img:max-w-full prose-p:text-[#334155] prose-p:leading-[1.9] w-full overflow-hidden">
            {Array.isArray(art.content) ? (
              art.content.map((paragraph, idx) => {
                const decoded = paragraph.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ');
                return <div key={idx} className="mb-8" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(decoded) }} />;
              })
            ) : (
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(art.content.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ')) }} />
            )}
          </div>

          {/* Tags */}
          {art.tags && art.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {art.tags.map(tag => (
                <Link key={tag} href={`/insights?tag=${encodeURIComponent(tag)}`} className="px-4 py-1.5 bg-slate-100 hover:bg-[#0d9488] text-slate-600 hover:text-white text-xs font-bold rounded-full border border-slate-200 hover:border-transparent transition-colors">
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* Previous / Next Post Links */}
          <div className="mt-16 pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-6">
            {art.prevPost ? (
              <Link href={`/insights/${art.prevPost.slug}`} className="group flex flex-col w-full sm:w-1/2 text-left hover:bg-slate-50 p-4 rounded-xl transition-colors border border-transparent hover:border-slate-100">
                <span className="flex items-center gap-1 text-[#64748b] text-xs font-bold uppercase tracking-widest mb-2 transition-transform group-hover:-translate-x-1"><ArrowLeft className="w-3 h-3" /> Previous Post</span>
                <span className="font-bold text-[#0f172a] group-hover:text-[#0d9488] line-clamp-2">{art.prevPost.title}</span>
              </Link>
            ) : <div className="w-full sm:w-1/2" />}

            {art.nextPost ? (
              <Link href={`/insights/${art.nextPost.slug}`} className="group flex flex-col w-full sm:w-1/2 text-right hover:bg-slate-50 p-4 rounded-xl transition-colors border border-transparent hover:border-slate-100 items-end">
                <span className="flex items-center gap-1 text-[#64748b] text-xs font-bold uppercase tracking-widest mb-2 transition-transform group-hover:translate-x-1">Next Post <ArrowLeft className="w-3 h-3 rotate-180" /></span>
                <span className="font-bold text-[#0f172a] group-hover:text-[#0d9488] line-clamp-2">{art.nextPost.title}</span>
              </Link>
            ) : <div className="w-full sm:w-1/2" />}
          </div>
        </div>

        {/* Right Sidebar: Sticky Info */}
        <aside className="w-full lg:w-[320px] shrink-0 flex flex-col gap-8 sticky top-32">

          {/* Author Profile Card */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 flex flex-col relative overflow-hidden group hover:shadow-md transition-shadow">
            {/* Top decorative cover banner */}
            <div className="h-16 w-full bg-gradient-to-r from-slate-800 to-[#0f172a]"></div>

            <div className="px-6 pb-6 flex flex-col items-center text-center">
              {/* Avatar overlapping the banner */}
              <div className="w-16 h-16 rounded-full bg-white mb-3 flex items-center justify-center relative z-10 shadow-lg border-4 border-white -mt-8">
                <div className="w-full h-full rounded-full bg-slate-50 flex items-center justify-center">
                  <User className="w-7 h-7 text-[#0d9488]" />
                </div>
              </div>

              <h4 className="font-extrabold text-[#0f172a] text-lg mb-0.5">{art.author.name}</h4>
              <p className="text-[10px] font-bold text-[#0d9488] uppercase tracking-widest mb-5">{art.author.role}</p>

              <Link href="/services" className="w-full py-2.5 rounded-xl bg-slate-50 text-[#0f172a] border border-slate-200 text-center font-bold text-xs hover:bg-[#0d9488] hover:text-white hover:border-transparent transition-all shadow-sm">
                View Our Services
              </Link>
            </div>
          </div>

          {/* Table of Contents (Dynamic) */}
          {tocItems.length > 0 && (
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
              <h4 className="font-bold text-[#0f172a] mb-4">Table of Contents</h4>
              <ul className="space-y-3">
                {tocItems.map((item, idx) => (
                  <li
                    key={item.id}
                    onClick={() => scrollToHeading(item.id)}
                    className={`text-sm text-slate-500 hover:text-[#0d9488] cursor-pointer flex gap-2 ${item.level === 3 ? 'ml-4' : ''}`}
                  >
                    <span className="text-[#0d9488] font-bold">{idx + 1}.</span> <span className="line-clamp-2">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Related Posts */}
          {recommended && recommended.length > 0 && (
            <div className="bg-transparent">
              <h4 className="font-bold text-[#0f172a] mb-5 text-lg">Related Posts</h4>
              <div className="flex flex-col gap-5">
                {recommended.map((rec) => (
                  <Link key={rec.slug} href={`/insights/${rec.slug}`} className="group flex gap-4 bg-white p-3 rounded-2xl border border-slate-100 hover:border-slate-200 shadow-sm hover:shadow-md transition-all">
                    {rec.featuredImage ? (
                      <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-slate-100">
                        <img src={rec.featuredImage} alt={rec.title} crossOrigin="anonymous" className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-teal-50 shrink-0 flex items-center justify-center">
                        <span className="text-[8px] font-bold text-[#0d9488] uppercase tracking-wider text-center">Insight</span>
                      </div>
                    )}
                    <div className="flex flex-col justify-center">
                      <h5 className="font-bold text-[#0f172a] text-sm group-hover:text-[#0d9488] transition-colors line-clamp-2 leading-snug mb-1">
                        {rec.title}
                      </h5>
                      <p className="text-xs text-slate-500">{rec.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </aside>
      </div>

      {/* ─── Unique Promise Section (Dark Mode Variant) ─────────────────────────── */}
      <section className="w-full bg-[#0a2540] text-white py-24 relative overflow-hidden">
        {/* Subtle background pattern/curves */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 1000 500" preserveAspectRatio="none">
            <path d="M-100,50 Q250,400 500,50 T1100,50" fill="none" stroke="#0d9488" strokeWidth="8" />
            <path d="M-100,450 Q250,100 500,450 T1100,450" fill="none" stroke="#0d9488" strokeWidth="4" />
          </svg>
        </div>

        <div className="max-w-[1000px] mx-auto px-6 relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div className="flex-1 text-center lg:text-left">
            <span
              ref={promiseSpanRef}
              className="text-[#2dd4bf] font-bold text-[10px] md:text-[11px] tracking-widest uppercase mb-4 block opacity-0"
            >
              Discover Compare All – The Future of Consultancy
            </span>
            <h2
              ref={promiseH2Ref}
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight mb-6 opacity-0"
            >
              The Compare All <br className="hidden lg:block" />Promise
            </h2>
            <p
              ref={promisePRef}
              className="text-slate-300 leading-relaxed text-sm md:text-base max-w-xl mx-auto lg:mx-0 opacity-0"
            >
              Whatever your requirement you can rest assured that the most suitable solution will be recommended without any bias to any one manufacturer. Compare All Ltd are delighted to quote on the sourcing and procurement of all your office needs.
            </p>

            <div
              ref={promiseCTARef}
              className="mt-8 opacity-0"
            >
              <Link href="/contact" className="inline-flex items-center gap-2 bg-[#0d9488] hover:bg-[#0f766e] text-white px-8 py-3 rounded-full font-bold transition-all shadow-lg hover:-translate-y-1">
                Speak To An Expert
              </Link>
            </div>
          </div>

          <div
            ref={promiseRightRef}
            className="flex-1 w-full max-w-md bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-3xl grid grid-cols-2 gap-6 opacity-0"
          >
            <div className="flex flex-col items-center text-center p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors">
              <div className="w-12 h-12 rounded-full bg-[#0d9488]/20 flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-[#2dd4bf]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
              </div>
              <span className="text-[10px] font-bold tracking-widest uppercase text-slate-300">Analysis</span>
            </div>
            <div className="flex flex-col items-center text-center p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors">
              <div className="w-12 h-12 rounded-full bg-[#0d9488]/20 flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-[#2dd4bf]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <span className="text-[10px] font-bold tracking-widest uppercase text-slate-300">Audit</span>
            </div>
            <div className="flex flex-col items-center text-center p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors">
              <div className="w-12 h-12 rounded-full bg-[#0d9488]/20 flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-[#2dd4bf]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <span className="text-[10px] font-bold tracking-widest uppercase text-slate-300">Procurement</span>
            </div>
            <div className="flex flex-col items-center text-center p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors">
              <div className="w-12 h-12 rounded-full bg-[#0d9488]/20 flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-[#2dd4bf]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
              </div>
              <span className="text-[10px] font-bold tracking-widest uppercase text-slate-300">Management</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
